import { AuthCallback, ForbiddenPage, RequireAuth, useRoles } from '@nrx/auth-ui';
import type { JSX } from 'react';
import { Navigate, Outlet, createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { AdminPortalLayout } from './components/admin-portal-layout.js';
import { AssistedEnrollmentPage } from './features/assisted-enrollment/assisted-enrollment-page.js';
import { DiscoveryPageEditor } from './features/cms/discovery-page-editor.js';
import { ContentBlocklistPage } from './features/content-blocklist/content-blocklist-page.js';
import { ContentPublishWorkflowPage } from './features/content-publish-workflow/content-publish-workflow-page.js';
import { AdminDashboardPage } from './features/dashboard/admin-dashboard-page.js';
import { EligibilityRecheckPage } from './features/eligibility-recheck/EligibilityRecheckPage.js';
import { EngagementConfigPage } from './features/engagement-config/engagement-config-page.js';
import { EscalationParamsPage } from './features/escalation-params/escalation-params-page.js';
import { EscalationDetailPage } from './features/escalation-queue/escalation-detail-page.js';
import { EscalationQueuePage } from './features/escalation-queue/escalation-queue-page.js';
import { ExceptionQueuePage } from './features/exception-queue/exception-queue-page.js';
import { LearningCmsPage } from './features/learning-cms/learning-cms-page.js';
import { MemberMergePage } from './features/member-merge/member-merge-page.js';
import { NutritionalTargetProfilesPage } from './features/nutritional-target-profiles/nutritional-target-profiles-page.js';
import { OosReviewQueuePage } from './features/oos-review-queue/oos-review-queue-page.js';
import { PromptLibraryPage } from './features/prompt-library/prompt-library-page.js';
import { RecipeCorpusPage } from './features/recipe-corpus/recipe-corpus-page.js';
import { ReferralDetailPage } from './features/referral-queue/unknown-provider/referral-detail-page.js';
import { UnknownProviderQueuePage } from './features/referral-queue/unknown-provider/unknown-provider-queue-page.js';
import { LogoutComplete } from './pages/auth/logout-complete.js';

/**
 * RequireAdminOrWaRole — Route guard for features that require staff:admin or staff:wa.
 *
 * Used by: member-merge, referral-queue/unknown-provider.
 * staff:rd and staff:md must NOT have access.
 */
function RequireAdminOrWaRole(): JSX.Element {
  const roles = useRoles();
  const hasAccess = roles.includes('staff:admin') || roles.includes('staff:wa');
  if (!hasAccess) {
    return <ForbiddenPage />;
  }
  return <Outlet />;
}

/**
 * RequireAdminOrClinicalLeadRole — Route guard for Learning CMS.
 *
 * staff:admin may manage all content; staff:clinical-lead authors and approves
 * knowledge checks. Other staff roles must NOT have access.
 */
function RequireAdminOrClinicalLeadRole(): JSX.Element {
  const roles = useRoles();
  const hasAccess = roles.includes('staff:admin') || roles.includes('staff:clinical-lead');
  if (!hasAccess) {
    return <ForbiddenPage />;
  }
  return <Outlet />;
}

/**
 * RequireRdOrMdRole — Route guard for Escalation Queue (Story 8.2).
 *
 * Only staff:rd and staff:md may access the RD escalation queue.
 * staff:admin, staff:wa, and other roles must NOT have access.
 */
function RequireRdOrMdRole(): JSX.Element {
  const roles = useRoles();
  const hasAccess = roles.includes('staff:rd') || roles.includes('staff:md');
  if (!hasAccess) {
    return <ForbiddenPage />;
  }
  return <Outlet />;
}

const router = createBrowserRouter([
  // Public routes
  {
    path: '/auth/callback',
    element: <AuthCallback />,
  },
  {
    path: '/auth/logout',
    element: <LogoutComplete />,
  },
  // Auth guard — any staff:* role required (AC #3: role-scoped landing)
  // RequireAuth renders <Outlet /> for authenticated users with required role prefix
  {
    path: '/',
    element: <RequireAuth requiredRolePrefix="staff:" />,
    children: [
      // Portal shell — sidebar + account menu sign-out (AC #6)
      {
        element: <AdminPortalLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: <AdminDashboardPage />,
          },
          // Role-gated routes — nested RequireAuth with role check
          {
            path: 'assisted-enrollment',
            element: <RequireAuth requiredRole="staff:wa" />,
            children: [
              {
                index: true,
                element: <AssistedEnrollmentPage />,
              },
            ],
          },
          {
            path: 'exception-queue',
            element: <RequireAuth requiredRole="staff:admin" />,
            children: [
              {
                index: true,
                element: <ExceptionQueuePage />,
              },
            ],
          },
          {
            path: 'recipe-corpus',
            element: <RequireAuth requiredRole="staff:admin" />,
            children: [
              {
                index: true,
                element: <RecipeCorpusPage />,
              },
            ],
          },
          {
            path: 'nutritional-target-profiles',
            element: <RequireAuth requiredRole="staff:admin" />,
            children: [
              {
                index: true,
                element: <NutritionalTargetProfilesPage />,
              },
            ],
          },
          // Education content publish workflow (Story 4.1): staff:admin only
          {
            path: 'content-publish-workflow',
            element: <RequireAuth requiredRole="staff:admin" />,
            children: [
              {
                index: true,
                element: <ContentPublishWorkflowPage />,
              },
            ],
          },
          // Engagement config (Story 3.6): staff:admin only
          {
            path: 'engagement-config',
            element: <RequireAuth requiredRole="staff:admin" />,
            children: [
              {
                index: true,
                element: <EngagementConfigPage />,
              },
            ],
          },
          // OOS Review Queue (Story 3.7): staff:rd and staff:admin (staff:rd is more permissive)
          {
            path: 'oos-review-queue',
            element: <RequireAuth requiredRole="staff:rd" />,
            children: [
              {
                index: true,
                element: <OosReviewQueuePage />,
              },
            ],
          },
          // Content Blocklist (Story 3.7): staff:admin only (write access)
          {
            path: 'content-blocklist',
            element: <RequireAuth requiredRole="staff:admin" />,
            children: [
              {
                index: true,
                element: <ContentBlocklistPage />,
              },
            ],
          },
          // Prompt library (Story 3.14): staff:admin only
          {
            path: 'prompt-library',
            element: <RequireAuth requiredRole="staff:admin" />,
            children: [
              {
                index: true,
                element: <PromptLibraryPage />,
              },
            ],
          },
          // Learning CMS (Story 4-3a): staff:admin or staff:clinical-lead
          {
            path: 'learning-cms',
            element: <RequireAdminOrClinicalLeadRole />,
            children: [
              {
                index: true,
                element: <LearningCmsPage />,
              },
            ],
          },
          // Member merge: staff:admin or staff:wa ONLY (AC #5)
          // staff:rd and staff:md must NOT have access to member merge.
          {
            path: 'member-merge',
            element: <RequireAdminOrWaRole />,
            children: [
              {
                index: true,
                element: <MemberMergePage />,
              },
            ],
          },
          {
            path: 'cms/discovery-page',
            element: <RequireAuth requiredRolePrefix="staff:" />,
            children: [
              {
                index: true,
                element: <DiscoveryPageEditor />,
              },
            ],
          },
          // Escalation params (Story 8.1): staff:admin only
          {
            path: 'escalation-params',
            element: <RequireAuth requiredRole="staff:admin" />,
            children: [
              {
                index: true,
                element: <EscalationParamsPage />,
              },
            ],
          },
          // Eligibility re-check results (Story 9.7): staff:admin (Clinical Ops surface).
          // The backend controllers gate on @HasRole('staff:admin'); the @nrx/rbac UserRole
          // union has no 'staff:clinical-ops' member, so the frontend guard must match the
          // backend role exactly.
          // PHI endpoint — memberId is displayed.
          {
            path: 'eligibility-rechecks',
            element: <RequireAuth requiredRole="staff:admin" />,
            children: [
              {
                index: true,
                element: <EligibilityRecheckPage />,
              },
            ],
          },
          // Escalation queue (Story 8.2): staff:rd or staff:md only
          {
            path: 'escalation-queue',
            element: <RequireRdOrMdRole />,
            children: [
              {
                index: true,
                element: <EscalationQueuePage />,
              },
              {
                path: ':escalationId',
                element: <EscalationDetailPage />,
              },
            ],
          },
          // Referral queue: staff:admin or staff:wa only
          {
            path: 'referral-queue',
            element: <RequireAdminOrWaRole />,
            children: [
              {
                path: 'unknown-provider',
                element: <UnknownProviderQueuePage />,
              },
              {
                path: 'unknown-provider/:referralId',
                element: <ReferralDetailPage />,
              },
            ],
          },
        ],
      },
    ],
  },
]);

export function App(): JSX.Element {
  return <RouterProvider router={router} />;
}
